import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Pencil, Trash2, Check, X } from 'lucide-react';

const TagManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#6366f1',
    quantityType: '',
    unitPrice: 0
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  // Inline edit state
  const [inlineEdit, setInlineEdit] = useState(null); // { tagId, field, value }

  const backendUrl = window.location.origin;
  const API = `${backendUrl}/api`;

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Etiketler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  // Inline update helper
  const handleInlineUpdate = async (tagId, field, value) => {
    const tag = tags.find(t => t.id === tagId);
    if (!tag) return;
    try {
      await axios.patch(`${API}/tags/${tagId}`, {
        name: tag.name,
        color: tag.color,
        quantityType: field === 'quantityType' ? value : (tag.quantityType || ''),
        unitPrice: field === 'unitPrice' ? parseFloat(value) || 0 : (tag.unitPrice ?? 0)
      });
      showToast('Güncellendi', 'success');
      fetchTags();
    } catch (error) {
      showToast('Güncellenemedi', 'error');
    }
    setInlineEdit(null);
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast('Etiket adı gerekli', 'error');
      return;
    }
    try {
      await axios.post(`${API}/tags`, {
        ...formData,
        unitPrice: parseFloat(formData.unitPrice) || 0
      });
      showToast('Etiket başarıyla oluşturuldu', 'success');
      setShowAddDialog(false);
      setFormData({ name: '', color: '#6366f1', quantityType: '', unitPrice: 0 });
      fetchTags();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Etiket oluşturulamadı';
      showToast(errorMessage, 'error');
    }
  };

  const handleEditTag = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast('Etiket adı gerekli', 'error');
      return;
    }
    try {
      await axios.patch(`${API}/tags/${editingTag.id}`, {
        ...formData,
        unitPrice: parseFloat(formData.unitPrice) || 0
      });
      showToast('Etiket başarıyla güncellendi', 'success');
      setEditingTag(null);
      setFormData({ name: '', color: '#6366f1', quantityType: '', unitPrice: 0 });
      fetchTags();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Etiket güncellenemedi';
      showToast(errorMessage, 'error');
    }
  };

  const handleDeleteTag = async (tagId, tagName, isDefault) => {
    if (isDefault) {
      showToast('Varsayılan etiket silinemez', 'error');
      return;
    }
    if (!window.confirm(`"${tagName}" etiketini silmek istediğinizden emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/tags/${tagId}`);
      showToast('Etiket başarıyla silindi', 'success');
      fetchTags();
    } catch (error) {
      let errorMessage = 'Etiket silinemedi';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
        if (errorMessage.includes('kullanılıyor') || errorMessage.includes('Kullanımda') || errorMessage.includes('görevde')) {
          errorMessage = 'Kullanımda olan Etiketler Silinemez!';
        }
      }
      showToast(errorMessage, 'error');
    }
  };

  const startEdit = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color,
      quantityType: tag.quantityType || '',
      unitPrice: tag.unitPrice ?? 0
    });
  };

  const cancelEdit = () => {
    setEditingTag(null);
    setFormData({ name: '', color: '#6366f1', quantityType: '', unitPrice: 0 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 space-y-4">
      {/* Toast */}
      {toast.show && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
              </svg>
            </div>
            <span className="relative">
              Kategori Yönetimi
              <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
                {tags.length.toLocaleString('tr-TR')}
              </span>
            </span>
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Görevler için kategoriler oluşturun ve yönetin
            {tags.length > 0 && (
              <span className="text-blue-600 font-medium text-xs sm:text-sm"> • {tags.length.toLocaleString('tr-TR')} kategori</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={() => navigate('/tasks')}
            className="px-3 py-2 sm:px-4 sm:py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm sm:text-base"
            data-testid="back-to-tasks-button"
          >
            ← Görev Sayfasına
          </button>
          <button
            onClick={() => setShowAddDialog(true)}
            className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition shadow-lg text-sm sm:text-base"
            data-testid="add-tag-button"
          >
            + Yeni Kategori
          </button>
        </div>
      </div>

      {/* Etiketler Listesi */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-2 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-bold text-gray-700 tracking-wider">
                  RENK
                </th>
                <th className="px-2 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-bold text-gray-700 tracking-wider">
                  KATEGORİ ADI
                </th>
                <th className="hidden sm:table-cell px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-bold text-gray-700 tracking-wider">
                  DURUM
                </th>
                <th className="px-2 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-bold text-gray-700 tracking-wider">
                  MİKTAR TÜRÜ
                </th>
                <th className="px-2 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-bold text-gray-700 tracking-wider">
                  BİRİM FİYATI
                </th>
                <th className="px-2 sm:px-4 lg:px-6 py-2 sm:py-3 text-right text-xs font-bold text-gray-700 tracking-wider">
                  İŞLEMLER
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tags.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <p className="text-sm font-medium">Henüz etiket yok</p>
                      <p className="text-xs text-gray-400">Yeni etiket ekleyerek başlayın</p>
                    </div>
                  </td>
                </tr>
              ) : (
                tags.map((tag) => (
                  <tr key={tag.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-colors" data-testid={`tag-row-${tag.id}`}>
                    <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 sm:border-3 border-gray-300 shadow-md"
                        style={{ backgroundColor: tag.color }}
                      />
                    </td>
                    <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4">
                      <div className="flex flex-col gap-1">
                        <div className="text-sm sm:text-base font-semibold text-gray-900 break-words">{tag.name}</div>
                        {tag.isDefault && (
                          <span className="sm:hidden inline-flex items-center w-fit px-2 py-0.5 text-xs font-bold rounded-md bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 border border-blue-300">
                            Varsayılan
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                      {tag.isDefault && (
                        <span className="px-2 sm:px-3 py-1 text-xs font-bold rounded-md bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 border border-blue-300">
                          Varsayılan
                        </span>
                      )}
                    </td>
                    {/* Miktar Türü - inline select */}
                    <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <select
                        value={tag.quantityType || ''}
                        onChange={(e) => handleInlineUpdate(tag.id, 'quantityType', e.target.value)}
                        className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 cursor-pointer"
                        data-testid={`tag-quantity-type-${tag.id}`}
                      >
                        <option value="">Seçiniz</option>
                        <option value="adet">Adet</option>
                        <option value="sure">Süre</option>
                      </select>
                    </td>
                    {/* Birim Fiyatı - inline edit */}
                    <td className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 whitespace-nowrap">
                      {inlineEdit?.tagId === tag.id && inlineEdit?.field === 'unitPrice' ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={inlineEdit.value}
                            onChange={(e) => setInlineEdit({ ...inlineEdit, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleInlineUpdate(tag.id, 'unitPrice', inlineEdit.value);
                              if (e.key === 'Escape') setInlineEdit(null);
                            }}
                            className="w-24 px-2 py-1.5 text-sm border-2 border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            autoFocus
                            data-testid={`tag-unit-price-input-${tag.id}`}
                          />
                          <button
                            onClick={() => handleInlineUpdate(tag.id, 'unitPrice', inlineEdit.value)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setInlineEdit(null)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setInlineEdit({ tagId: tag.id, field: 'unitPrice', value: tag.unitPrice ?? 0 })}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-purple-300 transition cursor-pointer min-w-[80px] text-left"
                          data-testid={`tag-unit-price-${tag.id}`}
                        >
                          {(tag.unitPrice ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </button>
                      )}
                    </td>
                    <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <button
                          onClick={() => startEdit(tag)}
                          className="p-2 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded-lg transition-all duration-200"
                          title="Düzenle"
                          data-testid={`tag-edit-${tag.id}`}
                        >
                          <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        {!tag.isDefault && (
                          <button
                            onClick={() => handleDeleteTag(tag.id, tag.name, tag.isDefault)}
                            className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-all duration-200"
                            title="Sil"
                            data-testid={`tag-delete-${tag.id}`}
                          >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Yeni Kategori Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-700 px-6 py-5">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                Yeni Kategori Ekle
              </h2>
            </div>

            <form onSubmit={handleAddTag} className="p-6 space-y-5">
              {/* Kategori Adı */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-200">
                <label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  Kategori Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white border-2 border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-medium"
                  placeholder="Örn: Animasyon, Tasarım, Yazılım"
                  required
                  data-testid="add-tag-name-input"
                />
              </div>

              {/* Renk */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl border-2 border-blue-200">
                <label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">Etiket Rengi</label>
                <div className="flex items-center gap-4 bg-white p-3 rounded-lg border-2 border-blue-300">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-16 h-16 rounded-lg cursor-pointer border-2 border-gray-300 shadow-md"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">Seçili Renk:</p>
                    <p className="text-lg font-bold text-gray-900">{formData.color}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-md" style={{ backgroundColor: formData.color }} />
                </div>
              </div>

              {/* Miktar Türü & Birim Fiyatı */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border-2 border-green-200">
                  <label className="text-sm font-semibold text-gray-800 mb-2 block">Miktar Türü</label>
                  <select
                    value={formData.quantityType}
                    onChange={(e) => setFormData({ ...formData, quantityType: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-medium"
                    data-testid="add-tag-quantity-type"
                  >
                    <option value="">Seçiniz</option>
                    <option value="adet">Adet</option>
                    <option value="sure">Süre</option>
                  </select>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl border-2 border-amber-200">
                  <label className="text-sm font-semibold text-gray-800 mb-2 block">Birim Fiyatı (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.unitPrice}
                    onChange={(e) => setFormData({ ...formData, unitPrice: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white border-2 border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 font-medium"
                    data-testid="add-tag-unit-price"
                  />
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-2xl border-t-2 border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddDialog(false);
                    setFormData({ name: '', color: '#6366f1', quantityType: '', unitPrice: 0 });
                  }}
                  className="flex-1 px-4 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 font-semibold transition"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-semibold shadow-lg transition"
                  data-testid="add-tag-submit"
                >
                  Ekle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Düzenleme Dialog */}
      {editingTag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 px-6 py-5">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Pencil className="w-6 h-6 text-white" />
                </div>
                Etiketi Düzenle
              </h2>
            </div>

            <form onSubmit={handleEditTag} className="p-6 space-y-5">
              {/* Kategori Adı */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border-2 border-blue-200">
                <label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  Kategori Adı {!editingTag.isDefault && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-4 py-3 bg-white border-2 rounded-lg font-medium ${
                    editingTag.isDefault
                      ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                      : 'border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  placeholder="Örn: Animasyon, Tasarım, Yazılım"
                  required
                  disabled={editingTag.isDefault}
                  data-testid="edit-tag-name-input"
                />
                {editingTag.isDefault && (
                  <p className="text-xs text-amber-600 mt-2 font-medium">Varsayılan etiketin adı değiştirilemez</p>
                )}
              </div>

              {/* Renk */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-200">
                <label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">Etiket Rengi</label>
                <div className="flex items-center gap-4 bg-white p-3 rounded-lg border-2 border-green-300">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-16 h-16 rounded-lg cursor-pointer border-2 border-gray-300 shadow-md"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">Seçili Renk:</p>
                    <p className="text-lg font-bold text-gray-900">{formData.color}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-md" style={{ backgroundColor: formData.color }} />
                </div>
              </div>

              {/* Miktar Türü & Birim Fiyatı */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-xl border-2 border-orange-200">
                  <label className="text-sm font-semibold text-gray-800 mb-2 block">Miktar Türü</label>
                  <select
                    value={formData.quantityType}
                    onChange={(e) => setFormData({ ...formData, quantityType: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white border-2 border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium"
                    data-testid="edit-tag-quantity-type"
                  >
                    <option value="">Seçiniz</option>
                    <option value="adet">Adet</option>
                    <option value="sure">Süre</option>
                  </select>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl border-2 border-amber-200">
                  <label className="text-sm font-semibold text-gray-800 mb-2 block">Birim Fiyatı (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.unitPrice}
                    onChange={(e) => setFormData({ ...formData, unitPrice: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white border-2 border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 font-medium"
                    data-testid="edit-tag-unit-price"
                  />
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-2xl border-t-2 border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="flex-1 px-4 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 font-semibold transition"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-semibold shadow-lg transition"
                  data-testid="edit-tag-submit"
                >
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagManagement;
